import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from './AuthContext';
import * as authService from '../services/authService';
import { errorNotification } from '../services/notificationService';

/**
 * AuthProvider Component
 * Manages authentication state and provides it to all child components
 */
export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is authenticated
  const isAuthenticated = user !== null;

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = authService.getSignedInUser();
        if (storedUser) {
          // Verify user is still valid by fetching fresh details
          try {
            const userDetails = await authService.getUserDetails();
            setUser(userDetails);
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(userDetails));
          } catch (error) {
            // If fetch fails, user might be logged out on server
            // Clear local state
            setUser(null);
            localStorage.removeItem('user');
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = useCallback(async (username, password) => {
    try {
      setIsLoading(true);
      const userData = await authService.login(username, password);
      setUser(userData);
      return userData;
    } catch (error) {
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await authService.signOut();
      setUser(null);
      navigate('/login', { replace: true });
    } catch (error) {
      // Even if signOut fails, clear local state
      setUser(null);
      localStorage.removeItem('user');
      navigate('/login', { replace: true });
      errorNotification('Error during logout');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Update user function
  const updateUser = useCallback((userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      localStorage.removeItem('user');
    }
  }, []);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    try {
      const userDetails = await authService.getUserDetails();
      setUser(userDetails);
      localStorage.setItem('user', JSON.stringify(userDetails));
      return userDetails;
    } catch (error) {
      // If refresh fails, user might be logged out
      setUser(null);
      localStorage.removeItem('user');
      throw error;
    }
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}







